import {Component, inject, OnDestroy, OnInit, computed, signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {ProblemItemAddCardComponent} from '@xmedical/clinical-shared';
import {MatDialog} from '@angular/material/dialog';
import {CoreService} from '../../../core/services/core.service';
import {Patient, Problem, ProblemListRepositoryService, UserSettings} from '@xmedical/core';
import {ProblemAddedEvent, ProblemDisplayItem, ProblemRemovedEvent} from '@xmedical/shared';
import {PatientProblemsStore, PatientStore} from '../../../core/state';

@Component({
    selector: 'app-patient-problem-widget',
    templateUrl: './problem-widget.component.html',
    styleUrls: ['./problem-widget.component.scss'],
    standalone: false
})
export class ProblemWidgetComponent implements OnInit, OnDestroy {

  error = '';
  loading = false;
  settings: UserSettings;
  private subscriptions: Subscription[] = [];

  private matDialog = inject(MatDialog);
  private problemsRepository = inject(ProblemListRepositoryService);
  private coreService = inject(CoreService);
  private patientStore = inject(PatientStore);
  protected problemsStore = inject(PatientProblemsStore);

  isProcessing = signal(false);

  canAdd = computed(() => this.coreService.hasPermission('Clinical.ProblemListing.Write'));
  canDelete = computed(() => this.coreService.hasPermission('Clinical.ProblemListing.Write'));

  problemItems = computed<ProblemDisplayItem[]>(() =>
    this.problemsStore.items().map(item => ({
      id: item.id,
      problem: item.problem,
      comment: item.comment,
      lastModifiedDate: item.lastModifiedDate,
      modifiedDateUtc: item.modifiedDateUtc,
      lastModifiedUser: item.lastModifiedUser
    }))
  );

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasProblems(): boolean {
    return this.problemsStore.count() > 0;
  }

  get patientId(): number {
    return this.patientStore.current()?.id ?? 0;
  }

  get patient(): Patient | null {
    return this.patientStore.current();
  }

  searchProblems$ = (query: string): Observable<Problem[]> => {
    return this.problemsRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.settings = this.coreService.settings();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onAddNewItem(searchTerm: string) {
    const dialogRef = this.matDialog.open(ProblemItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
      data: { searchTerm }
    });

    // Card returns the form value (no id). Persist to the master problem
    // list to obtain an id, then attach to the patient via the store.
    this.subscriptions.push(dialogRef.afterClosed().pipe(
      filter((v): v is Problem => !!v),
      switchMap(problem => this.problemsRepository.createProblemItem(problem).pipe(
        map(id => ({ ...problem, id } as Problem)),
      )),
    ).subscribe(problem => this.onItemSelected(problem)));
  }

  onItemAdded(event: ProblemAddedEvent) {
    const problem = event.item as Problem;
    problem.comment = event.comment;
    this.onItemSelected(problem);
  }

  onItemSelected(problem: Problem) {
    problem.createdUser = this.coreService.currentUser;
    problem.lastModifiedUser = this.coreService.currentUser;
    problem.createdDate = new Date();
    problem.createdDateUtc = new Date();
    problem.lastModifiedDate = new Date();
    problem.modifiedDateUtc = new Date();
    this.error = '';
    this.subscriptions.push(this.problemsStore.add(problem).subscribe({
      error: () => {
        this.error = 'Could not add new problem item to this patient';
      }
    }));
  }

  onItemRemoved(event: ProblemRemovedEvent) {
    const id = event.item.id;
    if (!id) return;
    this.isProcessing.set(true);
    this.subscriptions.push(this.problemsStore.remove(id).subscribe({
      next: () => this.isProcessing.set(false),
      error: () => {
        this.coreService.showErrorToast('Could not remove problem item');
        this.isProcessing.set(false);
      }
    }));
  }
}
